import ContentEditable from 'react-contenteditable';
import { useState, KeyboardEvent, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Send } from 'lucide-react';
import { testGuardrailsConfig } from '@/services/guardrails/test-guardrails-config';
import { stripHtmlTags } from '@/utils/strip-html-tags';
import { FailedState } from './failed-state';
import { SucceedState } from './succeed-state';
import { LoadingSpinner } from '@/components/loading-spinner';
import { useNotification } from '@/components/notifications';
import classNames from 'classnames';

interface Violation {
  name: string;
  reason: string;
}

export const Prompt = ({ disabled }: { disabled: boolean }) => {
  const notification = useNotification();
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingSucceed, setIsCheckingSucceed] = useState(false);
  const [violations, setViolations] = useState<Violation[]>([]);
  const valueRef = useRef('');

  const handleSendPrompt = async () => {
    setIsLoading(true);
    setIsCheckingSucceed(false);
    setViolations([]);

    try {
      const result = await testGuardrailsConfig({
        userQuery: stripHtmlTags(valueRef?.current),
      });

      if (result?.detail) {
        notification.error(result.detail);
        return;
      }

      if (result?.result === 'pass') {
        setIsCheckingSucceed(true);
      } else {
        setViolations(result?.failures || []);
      }
    } catch (error) {
      console.error('Test guardrails config error:', error);
      notification.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.code === 'Enter' && !event.shiftKey) {
      event.preventDefault();

      handleSendPrompt();
    }
  };

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-center pl-5 pr-2 py-2 rounded-md border border-zinc-200 dark:border-zinc-700 bg-transparent dark:bg-zinc-800 shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-zinc-400 dark:focus-visible:ring-zinc-500 disabled:cursor-not-allowed disabled:opacity-50'>
        <ContentEditable
          html={valueRef.current}
          disabled={disabled}
          onKeyDown={handleKeyDown}
          aria-placeholder={
            disabled
              ? 'Please activate at least one guardrail below to test a prompt'
              : 'Write a prompt here to test your configurations'
          }
          className={classNames(
            'resize-none h-full w-full placeholder:text-zinc-500 dark:placeholder:text-zinc-400 focus-visible:outline-none text-zinc-900 dark:text-zinc-100',
            {
              'opacity-50 cursor-not-allowed': disabled,
            },
          )}
          onChange={(event) => {
            const value =
              event.target.value === '<br>' ? '' : event.target.value;
            valueRef.current = value;
            setPrompt(value);
          }}
        />
        <Button
          disabled={!prompt.trim() || isLoading || disabled}
          variant='default'
          size='icon'
          onClick={handleSendPrompt}
          className='self-end'
        >
          {isLoading ? <LoadingSpinner /> : <Send />}
        </Button>
      </div>
      {violations.length > 0 && (
        <FailedState violations={violations} setViolations={setViolations} />
      )}
      {isCheckingSucceed && (
        <SucceedState setIsCheckingSucceed={setIsCheckingSucceed} />
      )}
    </div>
  );
};
